import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map } from 'rxjs';

export interface SelectedCurrency {
  id: number;
  code: string;
  symbol: string;
}

/**
 * Contexto reactivo para manejo de moneda y locale ligados al idioma.
 * Cambiar el locale dispara selección de moneda según mapa interno.
 */
@Injectable({ providedIn: 'root' })
export class CurrencyContextService {
  // Mapa simple idioma/locale -> moneda (ajusta según IDs reales)
  private readonly LOCALE_CURRENCY_MAP: Record<string, SelectedCurrency> = {
    'es-ES': { id: 7, code: 'EUR', symbol: '€' },
    'en-US': { id: 1, code: 'USD', symbol: '$' },
    // Agrega otros locales aquí
  };

  private localeSubject = new BehaviorSubject<string>('es-ES');

  /**
   * La moneda arranca en USD, no en la del locale.
   *
   * El idioma y la moneda son cosas distintas: el sitio esta en espanol, pero
   * Arcadia cotiza y liquida en dolares —es lo que guarda la orden—. Atar la
   * moneda al locale le mostraba EUR a un cliente venezolano o colombiano solo
   * por navegar en espanol.
   *
   * Cambiar el idioma a mano SI sigue ajustando la moneda: eso es una decision
   * del cliente, no un default.
   */
  private currencySubject = new BehaviorSubject<SelectedCurrency>(this.LOCALE_CURRENCY_MAP['en-US']);

  locale$: Observable<string> = this.localeSubject.asObservable();
  selectedCurrency$: Observable<SelectedCurrency> = this.currencySubject.asObservable();
  selectedCurrencyId$: Observable<number> = this.selectedCurrency$.pipe(map((c) => c.id));

  /** Moneda actualmente seleccionada (valor sincrónico). */
  get currentCurrency(): SelectedCurrency { return this.currencySubject.getValue(); }

  /** Cambia manualmente la moneda (independiente del locale). */
  setCurrency(currency: SelectedCurrency) {
    this.currencySubject.next(currency);
  }

  /** Cambia locale y ajusta moneda si existe mapping. */
  setLocale(locale: string) {
    this.localeSubject.next(locale);
    const mapped = this.LOCALE_CURRENCY_MAP[locale];
    if (mapped) {
      this.currencySubject.next(mapped);
    }
  }

  /** Permite extender el mapa en runtime si hiciera falta. */
  registerLocaleCurrency(locale: string, currency: SelectedCurrency) {
    this.LOCALE_CURRENCY_MAP[locale] = currency;
  }
}
